/**
 * SettingsPanel - Right panel showing clip settings or project info
 * Includes delete functionality and working settings sliders
 */
import { useState } from 'react';
import { Settings, Film, Trash2, ExternalLink } from 'lucide-react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Format time for display
const formatTimecode = (ms) => {
  if (!ms && ms !== 0) return '--:--';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const frames = Math.floor((ms % 1000) / (1000 / 24));
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')};${String(frames).padStart(2, '0')}`;
};

// Convert dB to gain and vice versa
const dbToGain = (db) => db <= -60 ? 0 : Math.pow(10, db / 20);
const gainToDb = (gain) => gain <= 0 ? -60 : 20 * Math.log10(gain);
const formatDb = (db) => db <= -60 ? '-∞' : `${db > 0 ? '+' : ''}${db.toFixed(1)}`;

// Editable value component - double click to edit
function EditableValue({ value, suffix = '%', min, max, onChange }) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());

  const handleDoubleClick = () => {
    setInputValue(value.toString());
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    const num = parseFloat(inputValue);
    if (!isNaN(num)) {
      const clamped = Math.max(min, Math.min(max, num));
      onChange(clamped);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setInputValue(value.toString());
    }
  };

  if (isEditing) {
    return (
      <Input
        type="number"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
        className="h-5 w-16 text-xs text-right px-1 py-0 bg-zinc-800 border-zinc-600"
        min={min}
        max={max}
      />
    );
  }

  return (
    <span 
      className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-300 hover:bg-zinc-800 px-1 rounded"
      onDoubleClick={handleDoubleClick}
      title="Double-click to edit"
    >
      {Math.round(value)}{suffix}
    </span>
  );
}

// Video clip settings component
function VideoClipSettings({ 
  selectedClip, 
  shot, 
  sceneGroups = [],
  onUpdateClipSettings, 
  onDeleteClip, 
  onChangeTake, 
  onEditShot,
  onCreateSceneGroup,
  onAddShotToSceneGroup,
  onRemoveShotFromSceneGroup,
  onViewProjectSettings
}) {
  const [newSceneName, setNewSceneName] = useState('');
  const currentSceneGroup = sceneGroups.find(g => g.shotIds.includes(shot?.id));
  
  return (
    <div className="space-y-4">
      {/* View Project Settings link */}
      <button
        onClick={onViewProjectSettings}
        className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
      >
        <Settings className="w-3 h-3" />
        View Project Settings
      </button>
      
      {/* Shot info */}
      {shot && (
        <div className="p-3 bg-zinc-800/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white">{shot.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEditShot?.(shot.id)}
              className="h-7 text-xs text-indigo-400 hover:text-indigo-300"
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              Edit
            </Button>
          </div>
          
          {/* Scene Assignment */}
          <div className="mt-2 mb-2">
            <Label className="text-xs text-zinc-400 mb-1.5 block">Scene</Label>
            <Select
              value={currentSceneGroup?.id || 'none'}
              onValueChange={(value) => {
                if (value === 'none') {
                  // Remove from current scene group
                  if (currentSceneGroup) {
                    onRemoveShotFromSceneGroup?.(currentSceneGroup.id, shot.id);
                  }
                } else if (value === 'new') {
                  // Will be handled by create dialog
                } else {
                  // Remove from old group and add to new
                  if (currentSceneGroup) {
                    onRemoveShotFromSceneGroup?.(currentSceneGroup.id, shot.id);
                  }
                  onAddShotToSceneGroup?.(value, shot.id);
                }
              }}
            >
              <SelectTrigger className="h-8 text-xs bg-zinc-900 border-zinc-700">
                <SelectValue placeholder="Assign to scene..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-xs text-zinc-500">No Scene</SelectItem>
                {sceneGroups.map((group) => (
                  <SelectItem key={group.id} value={group.id} className="text-xs">
                    <span className="flex items-center gap-2" title={group.name.length > 15 ? group.name : undefined}>
                      <span 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: group.color }}
                      />
                      {group.name.length > 15 ? `${group.name.substring(0, 15)}...` : group.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Quick create new scene group */}
            <div className="flex gap-1 mt-2">
              <Input
                placeholder="New scene name..."
                value={newSceneName}
                onChange={(e) => setNewSceneName(e.target.value)}
                className="h-7 text-xs bg-zinc-900 border-zinc-700"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSceneName.trim()) {
                    const group = onCreateSceneGroup?.(newSceneName.trim(), [shot.id]);
                    setNewSceneName('');
                  }
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs px-2"
                disabled={!newSceneName.trim()}
                onClick={() => {
                  if (newSceneName.trim()) {
                    onCreateSceneGroup?.(newSceneName.trim(), [shot.id]);
                    setNewSceneName('');
                  }
                }}
              >
                +
              </Button>
            </div>
          </div>
          
          {/* Clip timing info */}
          <div className="text-[10px] text-zinc-500 space-y-0.5">
            <div>Start: {formatTimecode(selectedClip?.start_ms)}</div>
            <div>Duration: {formatTimecode(selectedClip?.duration_ms)}</div>
          </div>
          
          {/* Take selector - show when takes exist */}
          {shot.takes?.length > 0 && (
            <div className="mt-3">
              <Label className="text-xs text-zinc-400 mb-1.5 block">Selected Take</Label>
              <Select
                value={selectedClip?.take_id || shot.printed_take_id || ''}
                onValueChange={(value) => onChangeTake?.(shot.id, value)}
              >
                <SelectTrigger className="h-8 text-xs bg-zinc-900 border-zinc-700">
                  <SelectValue placeholder="Select take..." />
                </SelectTrigger>
                <SelectContent>
                  {shot.takes.map((take, index) => (
                    <SelectItem key={take.id} value={take.id} className="text-xs">
                      Take {index + 1} - {take.type || 'Action'}
                      {take.id === shot.printed_take_id && ' ★'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
      
      {/* Blank clip info */}
      {selectedClip?.is_blank && (
        <div className="p-3 bg-zinc-800/50 rounded-lg">
          <span className="text-sm font-medium text-white">Blank Clip</span>
          <div className="text-[10px] text-zinc-500 mt-1">
            <div>Duration: {formatTimecode(selectedClip?.duration_ms)}</div>
          </div>
        </div>
      )}
      
      {/* Active toggle */}
      <div className="flex items-center justify-between">
        <Label className="text-xs text-zinc-400">Active</Label>
        <Switch
          checked={selectedClip?.settings?.active !== false}
          onCheckedChange={(checked) => 
            onUpdateClipSettings?.(selectedClip.id, 'video', { active: checked })
          }
        />
      </div>
      
      {/* Volume (dB) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-zinc-400">Volume</Label>
          <span className="text-xs text-zinc-300 font-mono">
            {formatDb(selectedClip?.settings?.volume ?? 0)} dB
          </span>
        </div>
        <Slider
          defaultValue={[((selectedClip?.settings?.volume ?? 0) + 60) / 72 * 100]}
          key={`vol-${selectedClip?.id}`}
          onValueCommit={([v]) => onUpdateClipSettings?.(selectedClip.id, 'video', { volume: Math.round((v / 100 * 72 - 60) * 10) / 10 })}
          min={0}
          max={100}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-[9px] text-zinc-500 mt-1">
          <span>-∞</span>
          <span>-24dB</span>
          <span>0dB</span>
          <span>+12dB</span>
        </div>
      </div>
      
      {/* Speed */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-zinc-400">Speed</Label>
          <EditableValue
            value={Math.round((selectedClip?.settings?.speed ?? 1) * 100)}
            min={10}
            max={1000}
            onChange={(v) => onUpdateClipSettings?.(selectedClip.id, 'video', { speed: v / 100 })}
          />
        </div>
        <Slider
          defaultValue={[Math.round((selectedClip?.settings?.speed ?? 1) * 100)]}
          key={`spd-${selectedClip?.id}`}
          onValueCommit={([v]) => onUpdateClipSettings?.(selectedClip.id, 'video', { speed: v / 100 })}
          min={10}
          max={1000}
          step={1}
          className="w-full"
        />
      </div>
      
      {/* Pan */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-zinc-400">Pan (L/R)</Label>
          <EditableValue
            value={Math.round((selectedClip?.settings?.pan ?? 0) * 100)}
            suffix={selectedClip?.settings?.pan === 0 ? ' C' : (selectedClip?.settings?.pan ?? 0) < 0 ? ' L' : ' R'}
            min={-100}
            max={100}
            onChange={(v) => onUpdateClipSettings?.(selectedClip.id, 'video', { pan: v / 100 })}
          />
        </div>
        <Slider
          defaultValue={[Math.round((selectedClip?.settings?.pan ?? 0) * 100)]}
          key={`pan-${selectedClip?.id}`}
          onValueCommit={([v]) => onUpdateClipSettings?.(selectedClip.id, 'video', { pan: v / 100 })}
          min={-100}
          max={100}
          step={1}
          className="w-full"
        />
      </div>
      
      {/* Opacity */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-zinc-400">Opacity</Label>
          <EditableValue
            value={Math.round((selectedClip?.settings?.transparency ?? 1) * 100)}
            min={0}
            max={100}
            onChange={(v) => onUpdateClipSettings?.(selectedClip.id, 'video', { transparency: v / 100 })}
          />
        </div>
        <Slider
          defaultValue={[Math.round((selectedClip?.settings?.transparency ?? 1) * 100)]}
          key={`opac-${selectedClip?.id}`}
          onValueCommit={([v]) => onUpdateClipSettings?.(selectedClip.id, 'video', { transparency: v / 100 })}
          min={0}
          max={100}
          step={1}
          className="w-full"
        />
      </div>
      
      {/* Scale */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-zinc-400">Scale</Label>
          <EditableValue
            value={Math.round((selectedClip?.settings?.scale ?? 1) * 100)}
            min={10}
            max={500}
            onChange={(v) => onUpdateClipSettings?.(selectedClip.id, 'video', { scale: v / 100 })}
          />
        </div>
        <Slider
          defaultValue={[Math.round((selectedClip?.settings?.scale ?? 1) * 100)]}
          key={`scale-${selectedClip?.id}`}
          onValueCommit={([v]) => onUpdateClipSettings?.(selectedClip.id, 'video', { scale: v / 100 })}
          min={50}
          max={200}
          step={1}
          className="w-full"
        />
      </div>
      
      {/* Delete button */}
      <div className="pt-4 border-t border-zinc-800">
        <Button
          variant="destructive"
          size="sm"
          className="w-full"
          onClick={() => onDeleteClip?.(selectedClip.id, 'video')}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete {selectedClip?.is_blank ? 'Blank' : 'Section'}
        </Button>
        <p className="text-[10px] text-zinc-600 mt-2 text-center">
          This will only delete this section, not the entire shot
        </p>
      </div>
    </div>
  );
}

// Audio clip settings component
function AudioClipSettings({ selectedClip, onUpdateClipSettings, onDeleteClip, onViewProjectSettings }) {
  return (
    <div className="space-y-4">
      {/* View Project Settings link */}
      <button
        onClick={onViewProjectSettings}
        className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
      >
        <Settings className="w-3 h-3" />
        View Project Settings
      </button>
      
      {/* Clip info */}
      <div className="p-3 bg-zinc-800/50 rounded-lg">
        <span className="text-sm font-medium text-white">{selectedClip?.name || 'Audio Clip'}</span>
        <div className="text-[10px] text-zinc-500 mt-1">
          <div>Start: {formatTimecode(selectedClip?.start_ms)}</div>
          <div>Duration: {formatTimecode(selectedClip?.duration_ms)}</div>
        </div>
      </div>
      
      {/* Active toggle */}
      <div className="flex items-center justify-between">
        <Label className="text-xs text-zinc-400">Active</Label>
        <Switch
          checked={selectedClip?.settings?.active !== false}
          onCheckedChange={(checked) => 
            onUpdateClipSettings?.(selectedClip.id, 'audio', { active: checked })
          }
        />
      </div>
      
      {/* Volume (dB) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-zinc-400">Volume</Label>
          <span className="text-xs text-zinc-300 font-mono">
            {formatDb(selectedClip?.settings?.volume ?? 0)} dB
          </span>
        </div>
        <Slider
          defaultValue={[((selectedClip?.settings?.volume ?? 0) + 60) / 72 * 100]}
          key={`audio-vol-${selectedClip?.id}`}
          onValueCommit={([v]) => onUpdateClipSettings?.(selectedClip.id, 'audio', { volume: Math.round((v / 100 * 72 - 60) * 10) / 10 })}
          min={0}
          max={100}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-[9px] text-zinc-500 mt-1">
          <span>-∞</span>
          <span>-24dB</span>
          <span>0dB</span>
          <span>+12dB</span>
        </div>
      </div>
      
      {/* Speed */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-zinc-400">Speed</Label>
          <EditableValue
            value={Math.round((selectedClip?.settings?.speed ?? 1) * 100)}
            min={10}
            max={1000}
            onChange={(v) => onUpdateClipSettings?.(selectedClip.id, 'audio', { speed: v / 100 })}
          />
        </div>
        <Slider
          defaultValue={[Math.round((selectedClip?.settings?.speed ?? 1) * 100)]}
          key={`audio-spd-${selectedClip?.id}`}
          onValueCommit={([v]) => onUpdateClipSettings?.(selectedClip.id, 'audio', { speed: v / 100 })}
          min={10}
          max={1000}
          step={1}
          className="w-full"
        />
      </div>
      
      {/* Pan */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-zinc-400">Pan (L/R)</Label>
          <EditableValue
            value={Math.round((selectedClip?.settings?.pan ?? 0) * 100)}
            suffix={selectedClip?.settings?.pan === 0 ? ' C' : (selectedClip?.settings?.pan ?? 0) < 0 ? ' L' : ' R'}
            min={-100}
            max={100}
            onChange={(v) => onUpdateClipSettings?.(selectedClip.id, 'audio', { pan: v / 100 })}
          />
        </div>
        <Slider
          defaultValue={[Math.round((selectedClip?.settings?.pan ?? 0) * 100)]}
          key={`audio-pan-${selectedClip?.id}`}
          onValueCommit={([v]) => onUpdateClipSettings?.(selectedClip.id, 'audio', { pan: v / 100 })}
          min={-100}
          max={100}
          step={1}
          className="w-full"
        />
      </div>
      
      {/* Delete button */}
      <div className="pt-4 border-t border-zinc-800">
        <Button
          variant="destructive"
          size="sm"
          className="w-full"
          onClick={() => onDeleteClip?.(selectedClip.id, 'audio')}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Audio Clip
        </Button>
      </div>
    </div>
  );
}

// Project info component (when nothing selected)
function ProjectInfo({ 
  totalDuration, 
  videoClipsCount, 
  audioTracksCount, 
  sceneGroups = [],
  excludedShots = [],
  onInitializeFromShots,
  onDeleteSceneGroup,
  onUpdateSceneGroup,
  onReAddShot
}) {
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingName, setEditingName] = useState('');

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs text-zinc-400 mb-1">Total Duration</p>
        <p className="text-sm text-white font-mono">{formatTimecode(totalDuration)}</p>
      </div>
      <div>
        <p className="text-xs text-zinc-400 mb-1">Shots</p>
        <p className="text-sm text-white">{videoClipsCount || 0}</p>
      </div>
      <div>
        <p className="text-xs text-zinc-400 mb-1">Audio Tracks</p>
        <p className="text-sm text-white">{audioTracksCount || 0}</p>
      </div>
      
      {/* Scenes */}
      {sceneGroups.length > 0 && (
        <div className="pt-4 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 mb-2">Scenes</p>
          <div className="space-y-2">
            {sceneGroups.map((group) => (
              <div 
                key={group.id} 
                className="flex items-center justify-between p-2 bg-zinc-800/50 rounded"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span 
                    className="w-3 h-3 rounded-full shrink-0" 
                    style={{ backgroundColor: group.color }}
                  />
                  {editingGroupId === group.id ? (
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => {
                        if (editingName.trim()) {
                          onUpdateSceneGroup?.(group.id, { name: editingName.trim() });
                        }
                        setEditingGroupId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (editingName.trim()) {
                            onUpdateSceneGroup?.(group.id, { name: editingName.trim() });
                          }
                          setEditingGroupId(null);
                        } else if (e.key === 'Escape') {
                          setEditingGroupId(null);
                        }
                      }}
                      autoFocus
                      className="h-6 text-xs bg-zinc-900 border-zinc-700 flex-1"
                    />
                  ) : (
                    <span 
                      className="text-xs text-white truncate cursor-pointer hover:text-indigo-300"
                      style={{ maxWidth: '120px' }}
                      onDoubleClick={() => {
                        setEditingGroupId(group.id);
                        setEditingName(group.name);
                      }}
                      title={group.name.length > 15 ? group.name : "Double-click to edit"}
                    >
                      {group.name.length > 15 ? `${group.name.substring(0, 15)}...` : group.name}
                    </span>
                  )}
                  <span className="text-[10px] text-zinc-500 shrink-0">
                    ({group.shotIds.length})
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400"
                  onClick={() => onDeleteSceneGroup?.(group.id)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Excluded/Removed Shots */}
      {excludedShots.length > 0 && (
        <div className="pt-4 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 mb-2">Removed Shots</p>
          <p className="text-[10px] text-zinc-600 mb-2">
            These shots were removed from the timeline. Click to re-add them.
          </p>
          <div className="space-y-1">
            {excludedShots.map((shot) => (
              <button
                key={shot.id}
                onClick={() => onReAddShot?.(shot.id)}
                className="w-full flex items-center gap-2 p-2 bg-zinc-800/30 hover:bg-zinc-700/50 rounded text-left transition-colors"
              >
                <Film className="w-3 h-3 text-zinc-500" />
                <span className="text-xs text-zinc-400 truncate flex-1">{shot.name}</span>
                <span className="text-[10px] text-emerald-400">+ Add</span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      <div className="pt-4 border-t border-zinc-800">
        <p className="text-xs text-zinc-500 mb-3">Quick Actions</p>
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start border-zinc-700 text-zinc-300 hover:text-white"
            onClick={onInitializeFromShots}
          >
            <Film className="w-4 h-4 mr-2" />
            Reload from Shots
          </Button>
        </div>
      </div>
      
      {/* Keyboard shortcuts */}
      <div className="pt-4 border-t border-zinc-800">
        <p className="text-xs text-zinc-500 mb-2">Keyboard Shortcuts</p>
        <div className="space-y-1 text-[10px] text-zinc-600">
          <div className="flex justify-between">
            <span>Play/Pause</span>
            <kbd className="px-1 bg-zinc-800 rounded">Space</kbd>
          </div>
          <div className="flex justify-between">
            <span>Frame step</span>
            <kbd className="px-1 bg-zinc-800 rounded">← →</kbd>
          </div>
          <div className="flex justify-between">
            <span>1 second</span>
            <kbd className="px-1 bg-zinc-800 rounded">Shift+← →</kbd>
          </div>
          <div className="flex justify-between">
            <span>Undo</span>
            <kbd className="px-1 bg-zinc-800 rounded">⌘Z</kbd>
          </div>
          <div className="flex justify-between">
            <span>Split</span>
            <kbd className="px-1 bg-zinc-800 rounded">⌘B</kbd>
          </div>
          <div className="flex justify-between">
            <span>Delete</span>
            <kbd className="px-1 bg-zinc-800 rounded">Delete</kbd>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main SettingsPanel component
export function SettingsPanel({
  selectedClip,
  selectedClipType,
  shot,
  totalDuration,
  videoClipsCount,
  audioTracksCount,
  sceneGroups = [],
  excludedShots = [],
  onUpdateClipSettings,
  onDeleteClip,
  onChangeTake,
  onEditShot,
  onInitializeFromShots,
  onCreateSceneGroup,
  onUpdateSceneGroup,
  onDeleteSceneGroup,
  onAddShotToSceneGroup,
  onRemoveShotFromSceneGroup,
  onReAddShot,
  onClearSelection
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-zinc-800">
        <h2 className="text-sm font-medium text-white flex items-center gap-2">
          <Settings className="w-4 h-4 text-indigo-400" />
          {selectedClip 
            ? selectedClipType === 'video' ? 'Shot Settings' : 'Audio Settings'
            : 'Project Settings'
          }
        </h2>
      </div>
      
      {/* Content */}
      <ScrollArea className="flex-1 p-3">
        {selectedClip && selectedClipType === 'video' ? (
          <VideoClipSettings
            selectedClip={selectedClip}
            shot={shot}
            sceneGroups={sceneGroups}
            onUpdateClipSettings={onUpdateClipSettings}
            onDeleteClip={onDeleteClip}
            onChangeTake={onChangeTake}
            onEditShot={onEditShot}
            onCreateSceneGroup={onCreateSceneGroup}
            onAddShotToSceneGroup={onAddShotToSceneGroup}
            onRemoveShotFromSceneGroup={onRemoveShotFromSceneGroup}
            onViewProjectSettings={onClearSelection}
          />
        ) : selectedClip && selectedClipType === 'audio' ? (
          <AudioClipSettings
            selectedClip={selectedClip}
            onUpdateClipSettings={onUpdateClipSettings}
            onDeleteClip={onDeleteClip}
            onViewProjectSettings={onClearSelection}
          />
        ) : (
          <ProjectInfo
            totalDuration={totalDuration}
            videoClipsCount={videoClipsCount}
            audioTracksCount={audioTracksCount}
            sceneGroups={sceneGroups}
            excludedShots={excludedShots}
            onInitializeFromShots={onInitializeFromShots}
            onDeleteSceneGroup={onDeleteSceneGroup}
            onUpdateSceneGroup={onUpdateSceneGroup}
            onReAddShot={onReAddShot}
          />
        )}
      </ScrollArea>
    </div>
  );
}
